import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useConfig } from "./config";
import { toast } from "react-hot-toast";
import "./Login.css";

function LoginVerify() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const successToastShown = useRef(false); // Track if we've shown the success toast

  // Get the redirect URL from the location state if available
  const redirectFromState = location.state?.from?.pathname;

  //console.log("Redirect URL from location state:", redirectFromState);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("No verification token provided");
        setVerifying(false);
        return;
      }

      try {
        //console.log("Verifying token:", token);
        const response = await fetch(
          `${config.API_BASE_URL}/api/auth/verify?token=${token}`,
          {
            credentials: "include", // Important for receiving cookies
          }
        );

        if (response.ok) {
          const data = await response.json();
          //console.log("Verification response:", data);
          //console.log("Redirect URL from response:", data.redirect_url);
          setSuccess(true);
          setVerifying(false);

          // Only show the success toast if we haven't shown it yet
          if (!successToastShown.current) {
            toast.success("Login successful!");
            successToastShown.current = true;
          }

          // Use the redirect URL from the API response, or from location state, or default to "/"
          const finalRedirect = data.redirect_url || redirectFromState || "/";
          //console.log("Final redirect URL:", finalRedirect);

          // Wait 1 second before redirecting
          setTimeout(() => {
            //console.log("Redirecting to:", finalRedirect);
            navigate(finalRedirect, { replace: true });
          }, 1000);
        } else {
          // Check if we're already authenticated despite the error
          const checkResponse = await fetch(
            `${config.API_BASE_URL}/api/auth/check`,
            {
              credentials: "include",
            }
          );

          if (checkResponse.ok) {
            // We're already authenticated, treat this as a success
            const data = await checkResponse.json();
            setSuccess(true);
            setVerifying(false);

            // Only show the success toast if we haven't shown it yet
            if (!successToastShown.current) {
              toast.success("Login successful!");
              successToastShown.current = true;
            }

            // Use the redirect URL from location state or default to "/"
            const finalRedirect = redirectFromState || "/";
            // console.log(
            //   "Final redirect URL (already authenticated):",
            //   finalRedirect
            // );

            // Wait 1 second before redirecting
            setTimeout(() => {
              //console.log("Redirecting to:", finalRedirect);
              navigate(finalRedirect, { replace: true });
            }, 1000);
          } else {
            // We're truly not authenticated
            const data = await response.json();
            setError(data.detail || "Verification failed");
            setVerifying(false);
            toast.error("Login failed. Please try again.");
          }
        }
      } catch (error) {
        //console.error("Verification error:", error);
        setError("An error occurred during verification");
        setVerifying(false);
        toast.error("Login failed. Please try again.");
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token, config, navigate, redirectFromState]);

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Verifying Login</h2>

        {verifying ? (
          <div className="verification-status">
            <div className="verification-loading"></div>
            <p>Verifying your login link...</p>
          </div>
        ) : success ? (
          <div className="verification-success">
            <p>Login successful!</p>
            <p>Redirecting you to the application...</p>
          </div>
        ) : error ? (
          <div className="verification-error">
            <p>Error: {error}</p>
            <p>The login link may have expired or is invalid.</p>
            <button
              className="login-button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to Login
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default LoginVerify;
